import { Link, createRoute } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";

import { ProtectedRoute } from "../auth/ProtectedRoute";
import { rootRoute } from "./__root";
import {
  useSMSThread,
  useSMSThreads,
  useSendSmsThread,
  type SMSPeriodFilter,
  type SMSThreadListItem,
} from "../queries";
import { IphoneSmsThread } from "../components/IphoneSmsThread";
import { GhostMark } from "../components/GhostMark";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: "/sms",
  component: SmsRoute,
});

function SmsRoute() {
  return (
    <ProtectedRoute requiredRoles={["owner", "gm", "sales"]}>
      <SmsScreen />
    </ProtectedRoute>
  );
}

function formatUsd(value: number | undefined) {
  return `$${Math.round(value || 0).toLocaleString()}`;
}

function relativeTime(value: string | undefined) {
  if (!value) return "";
  const stamp = new Date(value.includes("T") ? value : `${value.replace(" ", "T")}Z`).getTime();
  if (Number.isNaN(stamp)) return value;
  const diffMs = Date.now() - stamp;
  const minutes = Math.max(0, Math.round(diffMs / 60000));
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  return days === 1 ? "yesterday" : `${days}d ago`;
}

function directionLabel(direction: SMSThreadListItem["last_direction"]) {
  if (direction === "inbound") return "Customer";
  if (direction === "outbound-reply") return "Internal alert";
  return "Sent";
}

function SmsScreen() {
  const [period, setPeriod] = useState<SMSPeriodFilter>("recent");
  const [quoteStatus, setQuoteStatus] = useState<"all" | "quote_created">("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const hasDateFilter = Boolean(fromDate || toDate);
  const threads = useSMSThreads(50, 0, {
    period,
    quoteStatus: quoteStatus === "quote_created" ? "quote_created" : undefined,
    days: 14,
    from: fromDate || undefined,
    to: toDate || undefined,
  });
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const items = useMemo(() => threads.data?.items || [], [threads.data]);

  useEffect(() => {
    if (!items.length) {
      if (selectedKey) setSelectedKey(null);
      return;
    }
    if (!selectedKey || !items.some((item) => item.thread_key === selectedKey)) {
      setSelectedKey(items[0].thread_key);
    }
  }, [items, selectedKey]);

  return (
    <section className="space-y-5">
      <div>
        <Badge variant="slate">SMS</Badge>
        <h1 className="mt-3 text-2xl font-semibold text-slate-950">SMS conversations</h1>
        <p className="mt-1 max-w-3xl text-sm text-slate-600">
          One thread per customer in a phone-style view: what we sent and what they replied, side by side in time.
        </p>
      </div>

      <div className="space-y-5">
        <Card>
          <CardHeader>
            <div className="space-y-4">
              <div className="flex items-center justify-between gap-3">
                <CardTitle>Conversations</CardTitle>
                <Badge variant="slate">{items.length}</Badge>
              </div>
              <SmsFilters
                period={period}
                quoteStatus={quoteStatus}
                fromDate={fromDate}
                toDate={toDate}
                hasDateFilter={hasDateFilter}
                onPeriodChange={(next) => {
                  setPeriod(next);
                  setFromDate("");
                  setToDate("");
                }}
                onQuoteStatusChange={setQuoteStatus}
                onFromDateChange={setFromDate}
                onToDateChange={setToDate}
                onClearDates={() => {
                  setFromDate("");
                  setToDate("");
                }}
              />
            </div>
          </CardHeader>
          <CardContent>
            <div className="max-h-[680px] space-y-2 overflow-y-auto pr-1">
              {threads.isLoading ? <EmptyLine label="Loading SMS conversations..." /> : null}
              {threads.isError ? <EmptyLine label="SMS conversations unavailable." /> : null}
              {!threads.isLoading && !threads.isError && items.length === 0 ? (
                <EmptyLine label="No SMS conversations found." />
              ) : null}
              {items.map((item) => {
                const selected = item.thread_key === selectedKey;
                return (
                  <div key={item.thread_key} className="space-y-2">
                    <ConversationButton
                      item={item}
                      selected={selected}
                      onClick={() => setSelectedKey(item.thread_key)}
                    />
                    {selected ? (
                      <SmsConversationDetail threadKey={selectedKey} selected={item} />
                    ) : null}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>
    </section>
  );
}

function SmsFilters({
  period,
  quoteStatus,
  fromDate,
  toDate,
  hasDateFilter,
  onPeriodChange,
  onQuoteStatusChange,
  onFromDateChange,
  onToDateChange,
  onClearDates,
}: {
  period: SMSPeriodFilter;
  quoteStatus: "all" | "quote_created";
  fromDate: string;
  toDate: string;
  hasDateFilter: boolean;
  onPeriodChange: (period: SMSPeriodFilter) => void;
  onQuoteStatusChange: (status: "all" | "quote_created") => void;
  onFromDateChange: (value: string) => void;
  onToDateChange: (value: string) => void;
  onClearDates: () => void;
}) {
  const periods: Array<{ value: SMSPeriodFilter; label: string; helper: string }> = [
    { value: "recent", label: "Recent", helper: "Last 14 days" },
    { value: "older", label: "Older", helper: "Before 14 days" },
    { value: "all", label: "All", helper: "No age filter" },
  ];

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 overflow-hidden rounded-md border border-slate-200 bg-white">
        {[
          { value: "all", label: "All quotes", helper: "Every SMS thread" },
          { value: "quote_created", label: "Open quotes", helper: "Status quote_created" },
        ].map((option) => {
          const active = quoteStatus === option.value;
          return (
            <button
              key={option.value}
              type="button"
              className={`min-w-0 px-2 py-2 text-left transition ${
                active ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"
              }`}
              onClick={() => onQuoteStatusChange(option.value as "all" | "quote_created")}
            >
              <span className="block text-xs font-semibold">{option.label}</span>
              <span className={`mt-0.5 block text-[10px] ${active ? "text-slate-200" : "text-slate-400"}`}>
                {option.helper}
              </span>
            </button>
          );
        })}
      </div>
      <div className="grid grid-cols-3 overflow-hidden rounded-md border border-slate-200 bg-white">
        {periods.map((option) => {
          const active = period === option.value && !hasDateFilter;
          return (
            <button
              key={option.value}
              type="button"
              className={`min-w-0 px-2 py-2 text-left transition ${
                active ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"
              }`}
              onClick={() => onPeriodChange(option.value)}
            >
              <span className="block text-xs font-semibold">{option.label}</span>
              <span className={`mt-0.5 block text-[10px] ${active ? "text-slate-200" : "text-slate-400"}`}>
                {option.helper}
              </span>
            </button>
          );
        })}
      </div>
      <div className="grid gap-2 sm:grid-cols-[1fr_1fr_auto]">
        <label className="text-xs font-medium text-slate-500">
          From
          <input
            type="date"
            value={fromDate}
            onChange={(event) => onFromDateChange(event.target.value)}
            className="mt-1 h-9 w-full rounded-md border border-slate-200 bg-white px-2 text-sm text-slate-800 outline-none focus:border-slate-400"
          />
        </label>
        <label className="text-xs font-medium text-slate-500">
          To
          <input
            type="date"
            value={toDate}
            onChange={(event) => onToDateChange(event.target.value)}
            className="mt-1 h-9 w-full rounded-md border border-slate-200 bg-white px-2 text-sm text-slate-800 outline-none focus:border-slate-400"
          />
        </label>
        <Button
          variant="secondary"
          className="self-end"
          disabled={!hasDateFilter}
          onClick={onClearDates}
        >
          Clear
        </Button>
      </div>
      {hasDateFilter ? (
        <div className="text-xs text-slate-500">Date filter active. Recent/Older is paused until dates are cleared.</div>
      ) : null}
    </div>
  );
}

function ConversationButton({
  item,
  selected,
  onClick,
}: {
  item: SMSThreadListItem;
  selected: boolean;
  onClick: () => void;
}) {
  const lastIsCustomerMessage = item.last_direction === "inbound";
  // Always expand the phone thread. Quote 360 is a sibling link (never nested in the button).
  return (
    <div
      className={`rounded-md border transition ${
        selected ? "border-slate-300 bg-slate-50" : "border-slate-100 bg-white hover:bg-slate-50"
      }`}
    >
      <div className="flex items-start gap-2 p-3">
        <button
          type="button"
          data-testid={`sms-thread-button-${item.thread_key}`}
          className="min-w-0 flex-1 cursor-pointer text-left"
          onClick={onClick}
        >
          <ConversationCardBody item={item} lastIsCustomerMessage={lastIsCustomerMessage} />
        </button>
        <div className="flex shrink-0 flex-col items-end gap-1 pt-0.5">
          {item.needs_attention ? <Badge variant="amber">Needs reply</Badge> : null}
          {item.has_stop ? <Badge variant="red">STOP</Badge> : null}
          {item.matched_quote_id ? (
            <Link
              to="/quotes/$quoteId"
              params={{ quoteId: item.matched_quote_id }}
              data-testid={`sms-thread-open-quote-${item.thread_key}`}
              className="rounded-md border border-slate-200 bg-white px-2 py-0.5 text-[11px] font-medium text-slate-700 hover:bg-slate-50"
            >
              Quote 360
            </Link>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function ConversationCardBody({
  item,
  lastIsCustomerMessage,
}: {
  item: SMSThreadListItem;
  lastIsCustomerMessage: boolean;
}) {
  return (
    <>
      <div className="min-w-0">
        <div className="truncate font-medium text-slate-950">{item.customer_name}<GhostMark ghosted={item.ghosted} /></div>
        <div className="mt-1 text-xs text-slate-500">
          {item.matched_quote_id ? `quote #${item.matched_quote_id}` : item.phone_masked}
          {item.quote_status ? ` · ${item.quote_status}` : ""}
        </div>
      </div>
      <div className="mt-2 line-clamp-2 text-xs text-slate-600">
        <span className="font-medium">{directionLabel(item.last_direction)}:</span>{" "}
        <span className={lastIsCustomerMessage ? "italic" : undefined}>{item.last_body}</span>
      </div>
      <div className="mt-2 flex items-center justify-between gap-3 text-xs text-slate-400">
        <span>{relativeTime(item.last_message_at)}</span>
        <span>{item.message_count} msgs</span>
      </div>
    </>
  );
}

function SmsConversationDetail({ threadKey, selected }: { threadKey: string | null; selected: SMSThreadListItem | null }) {
  const thread = useSMSThread(threadKey);
  const sendThreadSms = useSendSmsThread(threadKey);
  const queryClient = useQueryClient();

  if (!threadKey || !selected) {
    return <div className="rounded-md border border-slate-200 bg-slate-50 p-6 text-sm text-slate-500">Select a conversation to see the full SMS timeline.</div>;
  }

  const detail = thread.data;
  const quoteId = detail?.matched_quote_id || selected.matched_quote_id;
  const customerName = detail?.customer_name || selected.customer_name;
  const phoneMasked = detail?.phone_masked || selected.phone_masked;
  const messages = detail?.messages || [];
  const canSend = !selected.has_stop;

  return (
    <section className="rounded-md border border-slate-200 bg-white shadow-sm" data-testid={`sms-thread-detail-${threadKey}`}>
      <div className="border-b border-slate-100 p-4">
        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
          <div>
            <CardTitle>{customerName}</CardTitle>
            <div className="mt-1 text-sm text-slate-500">
              {quoteId ? `quote #${quoteId}` : phoneMasked}
              {selected.quote_status ? ` · ${selected.quote_status}` : ""}
              {selected.quote_value ? ` · ${formatUsd(selected.quote_value)}` : ""}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {selected.needs_attention ? <Badge variant="amber">Needs reply</Badge> : null}
            {selected.has_stop ? <Badge variant="red">STOP</Badge> : null}
            {quoteId ? (
              <Link to="/quotes/$quoteId" params={{ quoteId }}>
                <Button variant="secondary" className="h-8">Open quote</Button>
              </Link>
            ) : null}
          </div>
        </div>
      </div>
      <div className="bg-slate-50 p-4">
        <IphoneSmsThread
          customerName={customerName}
          phoneMasked={phoneMasked}
          messages={messages}
          hasStop={selected.has_stop}
          quoteId={quoteId}
          isLoading={thread.isLoading}
          isError={thread.isError}
          canSend={canSend}
          isSending={sendThreadSms.isPending}
          sendError={sendThreadSms.isError ? String(sendThreadSms.error.message) : null}
          onSend={async (body) => {
            await sendThreadSms.mutateAsync({ body, quoteId: quoteId || null });
            await Promise.all([
              queryClient.invalidateQueries({ queryKey: ["sms", "thread", threadKey] }),
              queryClient.invalidateQueries({ queryKey: ["sms", "threads"] }),
            ]);
          }}
        />
      </div>
    </section>
  );
}

function EmptyLine({ label }: { label: string }) {
  return (
    <div className="rounded-md border border-slate-100 bg-slate-50 p-4 text-center text-sm text-slate-500">
      {label}
    </div>
  );
}
